import React, { useState, useMemo } from 'react';
import {
  Box, Container, Grid, Card, CardContent, Typography, Avatar, Chip,
  TextField, MenuItem, Button, InputAdornment, Divider, Paper,
} from '@mui/material';
import {
  Search, CalendarMonth, Person, ArrowForward,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { doctors } from '../data/hospitalData';
import PageHero from '../components/common/PageHero';

// ─── Sub-category cards ───────────────────────────────────────────────────────

const infoCards = [
  { icon: '👨‍⚕️', titleEn: 'Surgical Specialists', titleFr: 'Spécialistes Chirurgicaux', bgColor: '#0B4F8A' },
  { icon: '👩‍⚕️', titleEn: 'Medical Specialists', titleFr: 'Spécialistes Médicaux', bgColor: '#1565C0' },
  { icon: '🔬', titleEn: 'Diagnostic Specialists', titleFr: 'Spécialistes Diagnostiques', bgColor: '#0277BD' },
  { icon: '🏥', titleEn: 'Book a Consultation', titleFr: 'Prendre Rendez-vous', bgColor: '#01579B' },
];

// ─── Doctor detail sidebar ────────────────────────────────────────────────────

const DoctorSidebar: React.FC<{ doctor: typeof doctors[0]; language: string; onAppt: () => void }> = ({ doctor, language, onAppt }) => (
  <Paper elevation={2} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 0, overflow: 'hidden', position: { md: 'sticky' }, top: { md: 90 } }}>
    <Box sx={{ height: 220, background: `linear-gradient(145deg, ${doctor.color}22 0%, ${doctor.color}44 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Avatar sx={{ width: 130, height: 130, bgcolor: doctor.color, fontSize: '2.5rem', fontWeight: 800, border: '4px solid white', boxShadow: '0 4px 24px rgba(0,0,0,0.18)' }}>
        {doctor.initials}
      </Avatar>
    </Box>
    <Box sx={{ p: 3 }}>
      <Typography variant="body2" color="text.secondary" fontWeight={600} mb={0.25}>
        {language === 'en' ? 'Senior Consultant' : 'Médecin Consultant Senior'}
      </Typography>
      <Typography variant="h6" fontWeight={800} color="text.primary" mb={0.5} lineHeight={1.3}>{doctor.name}</Typography>
      <Chip label={language === 'en' ? doctor.specialtyEn : doctor.specialtyFr} size="small"
        sx={{ bgcolor: `${doctor.color}18`, color: doctor.color, fontWeight: 700, fontSize: '0.72rem', mb: 1.5 }} />
      <Divider sx={{ my: 2 }} />
      {[
        language === 'en' ? doctor.specialtyEn : doctor.specialtyFr,
        `${doctor.experience} ${language === 'en' ? 'years experience' : 'ans d\'expérience'}`,
        `${language === 'en' ? 'Available:' : 'Disponible :'} ${doctor.availability}`,
      ].map((s, i) => (
        <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 0.75 }}>
          <Person sx={{ fontSize: 15, color: 'primary.main', mt: 0.3, flexShrink: 0 }} />
          <Typography variant="body2" color="text.secondary" lineHeight={1.5}>{s}</Typography>
        </Box>
      ))}
      <Divider sx={{ my: 2 }} />
      <Button variant="contained" size="small" fullWidth startIcon={<CalendarMonth />} onClick={onAppt}
        sx={{ textTransform: 'none', borderRadius: 0, fontWeight: 700 }}>
        {language === 'en' ? 'Book with this Doctor' : 'Consultation avec ce Médecin'}
      </Button>
    </Box>
  </Paper>
);

// ─── Doctor list card ─────────────────────────────────────────────────────────

const DoctorCard: React.FC<{
  doctor: typeof doctors[0];
  language: string;
  isSelected: boolean;
  onSelect: () => void;
  onAppt: () => void;
}> = ({ doctor, language, isSelected, onSelect, onAppt }) => (
  <Card
    onClick={onSelect}
    sx={{
      height: '100%', cursor: 'pointer',
      border: '2px solid',
      borderColor: isSelected ? 'primary.main' : 'divider',
      borderRadius: 0,
      transition: 'all 0.2s',
      '&:hover': { borderColor: 'primary.main', boxShadow: 4 },
    }}
  >
    <CardContent sx={{ p: 3, display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <Avatar sx={{ width: 64, height: 64, bgcolor: doctor.color, fontSize: '1.4rem', fontWeight: 800, flexShrink: 0 }}>
          {doctor.initials}
        </Avatar>
        <Box>
          <Typography variant="subtitle1" fontWeight={800} lineHeight={1.2} mb={0.5}>{doctor.name}</Typography>
          <Chip label={language === 'en' ? doctor.specialtyEn : doctor.specialtyFr} size="small"
            sx={{ bgcolor: `${doctor.color}18`, color: doctor.color, fontWeight: 600, fontSize: '0.7rem' }} />
        </Box>
      </Box>

      <Box sx={{ borderLeft: '3px solid', borderColor: `${doctor.color}`, pl: 1.5, mb: 2 }}>
        <Typography variant="body2" color="text.secondary" lineHeight={1.75}>
          {language === 'en' ? doctor.bioEn : doctor.bioFr}
        </Typography>
      </Box>

      <Box sx={{ mt: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="caption" color="text.disabled">
          {doctor.experience} {language === 'en' ? 'yrs' : 'ans'} · {doctor.availability}
        </Typography>
        <Button size="small" startIcon={<CalendarMonth />} variant="outlined"
          onClick={(e) => { e.stopPropagation(); onAppt(); }}
          sx={{ borderColor: doctor.color, color: doctor.color, textTransform: 'none', borderRadius: 0, fontSize: '0.75rem',
            '&:hover': { bgcolor: `${doctor.color}10` } }}>
          {language === 'en' ? 'Book' : 'Réserver'}
        </Button>
      </Box>
    </CardContent>
  </Card>
);

// ─── Main Page ────────────────────────────────────────────────────────────────

const DoctorsPage: React.FC = () => {
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [specialty, setSpecialty] = useState('all');
  const [selectedDoctor, setSelectedDoctor] = useState<typeof doctors[0]>(doctors[0]);

  const specialties = useMemo(() => {
    const all = language === 'en'
      ? [...new Set(doctors.map(d => d.specialtyEn))]
      : [...new Set(doctors.map(d => d.specialtyFr))];
    return all;
  }, [language]);

  const filtered = useMemo(() => {
    return doctors.filter((doc) => {
      const name = doc.name.toLowerCase();
      const spec = language === 'en' ? doc.specialtyEn : doc.specialtyFr;
      const searchMatch = search === '' || name.includes(search.toLowerCase()) || spec.toLowerCase().includes(search.toLowerCase());
      const specMatch = specialty === 'all' || spec === specialty;
      return searchMatch && specMatch;
    });
  }, [search, specialty, language]);

  return (
    <Box sx={{ bgcolor: 'background.default' }}>

      {/* ── Hero ── */}
      <PageHero
        title={t.doctors.title}
        subtitle={t.doctors.subtitle}
        breadcrumbs={[
          { label: language === 'en' ? 'Home' : 'Accueil', path: '/' },
          { label: language === 'en' ? 'Our Doctors' : 'Nos Médecins' },
        ]}
        accentColor="#0B4F8A"
      />

      {/* ── Sub-category cards ── */}
      <Container maxWidth="xl" sx={{ py: 4 }}>
        <Grid container spacing={2}>
          {infoCards.map((card, i) => (
            <Grid item xs={6} sm={3} key={i}>
              <Box sx={{ border: '1px solid', borderColor: 'divider', overflow: 'hidden', height: '100%' }}>
                <Box sx={{ height: 160, background: `linear-gradient(135deg, ${card.bgColor}CC 0%, ${card.bgColor}88 100%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '3.5rem' }}>
                  {card.icon}
                </Box>
                <Box sx={{ p: 2 }}>
                  <Box sx={{ borderLeft: '3px solid', borderColor: 'primary.main', pl: 1.5, mb: 1.5 }}>
                    <Typography variant="subtitle1" fontWeight={700} lineHeight={1.3}>
                      {language === 'en' ? card.titleEn : card.titleFr}
                    </Typography>
                  </Box>
                  <Typography onClick={() => navigate('/appointment')}
                    sx={{ color: 'primary.main', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 0.5, '&:hover': { textDecoration: 'underline' } }}>
                    {language === 'en' ? 'Learn more' : 'En savoir plus'} <ArrowForward sx={{ fontSize: 14 }} />
                  </Typography>
                </Box>
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* ── Main content + Sidebar ── */}
      <Container maxWidth="xl" sx={{ pb: 8 }}>
        <Grid container spacing={4}>

          {/* Left — doctor list */}
          <Grid item xs={12} md={8}>

            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.04em', mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
              {language === 'en' ? 'OUR MEDICAL SPECIALISTS & SENIOR PHYSICIANS' : 'NOS SPÉCIALISTES MÉDICAUX & MÉDECINS SENIORS'}
            </Typography>

            <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={3}>
              {language === 'en'
                ? 'Hôpital Régional de Bafoussam is home to a distinguished team of over 80 medical specialists, each bringing deep expertise, continuous professional development, and a commitment to delivering the highest standards of patient-centered care across all clinical disciplines.'
                : 'L\'Hôpital Régional de Bafoussam abrite une équipe distinguée de plus de 80 spécialistes médicaux, chacun apportant une expertise approfondie, un développement professionnel continu et un engagement à dispenser les plus hauts standards de soins centrés sur le patient dans toutes les disciplines cliniques.'}
            </Typography>

            {/* Filters */}
            <Box sx={{ display: 'flex', gap: 2, mb: 4, flexWrap: 'wrap' }}>
              <TextField size="small" placeholder={t.doctors.searchPlaceholder} value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }}
                sx={{ width: { xs: '100%', sm: 280 }, '& .MuiOutlinedInput-root': { borderRadius: 0 } }} />
              <TextField select size="small" value={specialty} onChange={(e) => setSpecialty(e.target.value)}
                sx={{ width: { xs: '100%', sm: 220 }, '& .MuiOutlinedInput-root': { borderRadius: 0 } }}>
                <MenuItem value="all">{t.common.allSpecialties}</MenuItem>
                {specialties.map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
              </TextField>
              {(search || specialty !== 'all') && (
                <Button variant="outlined" size="small" onClick={() => { setSearch(''); setSpecialty('all'); }}
                  sx={{ borderRadius: 0 }}>
                  {language === 'en' ? 'Clear' : 'Effacer'}
                </Button>
              )}
            </Box>

            {filtered.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <Typography color="text.secondary">
                  {language === 'en' ? 'No doctors found.' : 'Aucun médecin trouvé.'}
                </Typography>
              </Box>
            ) : (
              <Grid container spacing={2.5}>
                {filtered.map((doctor) => (
                  <Grid item xs={12} sm={6} key={doctor.id}>
                    <DoctorCard
                      doctor={doctor}
                      language={language}
                      isSelected={selectedDoctor.id === doctor.id}
                      onSelect={() => setSelectedDoctor(doctor)}
                      onAppt={() => navigate('/appointment')}
                    />
                  </Grid>
                ))}
              </Grid>
            )}

            <Divider sx={{ my: 4 }} />

            {/* Professional standards section */}
            <Typography variant="h5" sx={{ color: 'primary.main', fontWeight: 800, letterSpacing: '0.04em', mb: 2, fontSize: { xs: '1rem', md: '1.25rem' } }}>
              {language === 'en' ? 'PROFESSIONAL STANDARDS & CONTINUOUS TRAINING' : 'NORMES PROFESSIONNELLES & FORMATION CONTINUE'}
            </Typography>
            <Typography variant="body1" color="text.secondary" lineHeight={1.9} mb={2}>
              {language === 'en'
                ? 'All physicians at Hôpital Régional de Bafoussam hold recognized medical degrees from accredited institutions in Cameroon, France, or other recognized medical schools. Each specialist participates in mandatory Continuing Medical Education (CME) programs, regular peer reviews, and monthly multidisciplinary case conferences to maintain and advance their clinical expertise.'
                : 'Tous les médecins de l\'Hôpital Régional de Bafoussam sont titulaires de diplômes médicaux reconnus d\'établissements accrédités au Cameroun, en France ou dans d\'autres facultés de médecine reconnues. Chaque spécialiste participe à des programmes obligatoires de Formation Médicale Continue (FMC), à des revues par les pairs régulières et à des conférences mensuelles de dossiers multidisciplinaires pour maintenir et développer son expertise clinique.'}
            </Typography>
            <Typography variant="body1" color="text.secondary" lineHeight={1.9}>
              {language === 'en'
                ? 'Our medical team is registered with the Cameroonian National Order of Physicians (Ordre National des Médecins du Cameroun) and adheres to the highest ethical standards of the medical profession, including patient confidentiality, informed consent, and evidence-based practice.'
                : 'Notre équipe médicale est inscrite à l\'Ordre National des Médecins du Cameroun et adhère aux plus hauts standards éthiques de la profession médicale, notamment la confidentialité du patient, le consentement éclairé et la pratique fondée sur des preuves.'}
            </Typography>
          </Grid>

          {/* Right — selected doctor sidebar */}
          <Grid item xs={12} md={4}>
            <DoctorSidebar doctor={selectedDoctor} language={language} onAppt={() => navigate('/appointment')} />

            <Box sx={{ mt: 3, p: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
              <Typography variant="subtitle2" fontWeight={700} mb={2} color="primary.main">
                {language === 'en' ? 'Book an Appointment' : 'Prendre Rendez-vous'}
              </Typography>
              <Typography variant="body2" color="text.secondary" lineHeight={1.7} mb={2}>
                {language === 'en'
                  ? 'Appointments can be scheduled Monday–Saturday. Urgent consultations are available 24/7 through our Emergency Department.'
                  : 'Les rendez-vous peuvent être programmés du lundi au samedi. Les consultations urgentes sont disponibles 24h/24 via notre service des urgences.'}
              </Typography>
              <Button variant="outlined" fullWidth onClick={() => navigate('/appointment')}
                sx={{ textTransform: 'none', borderRadius: 0 }}>
                {language === 'en' ? 'View All Appointment Options' : 'Toutes les Options de Rendez-vous'}
              </Button>
            </Box>
          </Grid>

        </Grid>
      </Container>
    </Box>
  );
};

export default DoctorsPage;
